// ============================================
// FAMUZA MARKET — Seller Shop Page
// Public page at /shop/:id
// Shareable link for sellers to post on WhatsApp/social
// ============================================

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { supabase } from '../supabase';

function parsePrice(val) {
  return parseFloat(String(val ?? '0').replace(/[^0-9.]/g, '')) || 0;
}

function timeAgo(dateStr) {
  const diff = Date.now() - new Date(dateStr);
  const mins = Math.floor(diff / 60000);
  const hrs  = Math.floor(diff / 3600000);
  const days = Math.floor(diff / 86400000);
  if (mins < 60) return `${mins}m ago`;
  if (hrs < 24)  return `${hrs}h ago`;
  return `${days}d ago`;
}

const WaIcon = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" style={{ flexShrink: 0 }}>
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/>
    <path d="M12 0C5.373 0 0 5.373 0 12c0 2.124.558 4.118 1.528 5.845L0 24l6.335-1.508A11.953 11.953 0 0012 24c6.627 0 12-5.373 12-12S18.627 0 12 0zm0 21.818a9.818 9.818 0 01-5.006-1.366l-.36-.213-3.724.977.994-3.634-.234-.374A9.818 9.818 0 1112 21.818z"/>
  </svg>
);

// ── Product Card ──────────────────────────────────────────────────────────────
function ProductCard({ item, navigate, formatPrice, whatsAppEnquiry, sellerPhone }) {
  const discount = item.old_price
    ? Math.round(((parsePrice(item.old_price) - parsePrice(item.price)) / parsePrice(item.old_price)) * 100)
    : null;
  const isNew = (Date.now() - new Date(item.created_at)) < 48 * 3600000;

  return (
    <div
      onClick={() => navigate(`/product/${item.id}`)}
      style={{ background: '#1E1E1E', borderRadius: 16, overflow: 'hidden', cursor: 'pointer', transition: 'transform 0.18s, box-shadow 0.18s', boxShadow: '0 2px 12px rgba(0,0,0,0.4)' }}
      onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-3px)'; e.currentTarget.style.boxShadow = '0 8px 24px rgba(0,0,0,0.6)'; }}
      onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 2px 12px rgba(0,0,0,0.4)'; }}
    >
      <div style={{ position: 'relative', paddingBottom: '80%', background: '#2A2A2A', overflow: 'hidden' }}>
        <img
          src={item.image_url} alt={item.title}
          style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }}
          onError={e => e.target.style.display = 'none'}
        />
        <div style={{ position: 'absolute', top: 7, left: 7, display: 'flex', flexDirection: 'column', gap: 4 }}>
          {discount && (
            <span style={{ background: '#E8622A', color: '#fff', fontSize: '0.62rem', fontWeight: 800, padding: '3px 8px', borderRadius: 100, boxShadow: '0 2px 8px rgba(232,98,42,0.6)' }}>
              -{discount}%
            </span>
          )}
          {isNew && (
            <span style={{ background: '#25D366', color: '#fff', fontSize: '0.62rem', fontWeight: 800, padding: '3px 8px', borderRadius: 100 }}>
              🔥 NEW
            </span>
          )}
        </div>
        {(item.stock === 1 || item.quantity === 1) && (
          <div style={{ position: 'absolute', bottom: 7, left: 7, background: 'rgba(0,0,0,0.85)', color: '#ff6b6b', fontSize: '0.6rem', fontWeight: 700, padding: '3px 8px', borderRadius: 100 }}>
            🔴 Only 1 left!
          </div>
        )}
      </div>
      <div style={{ padding: '0.7rem' }}>
        <div style={{ fontFamily: 'Syne, sans-serif', fontSize: '1.15rem', fontWeight: 800, color: '#E8622A', lineHeight: 1, marginBottom: 4 }}>
          {formatPrice(item.price)}
          {item.old_price && (
            <span style={{ fontSize: '0.65rem', color: 'rgba(245,240,232,0.3)', textDecoration: 'line-through', marginLeft: 6, fontWeight: 400 }}>
              {formatPrice(item.old_price)}
            </span>
          )}
        </div>
        <div style={{ fontSize: '0.78rem', fontWeight: 500, color: 'rgba(245,240,232,0.9)', lineHeight: 1.3, marginBottom: 6, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {item.title}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 4 }}>
          <div style={{ fontSize: '0.6rem', color: 'rgba(245,240,232,0.35)', lineHeight: 1.5 }}>
            <div style={{ color: '#E8622A', fontWeight: 600, textTransform: 'uppercase', fontSize: '0.55rem' }}>{item.category}</div>
            <div>🕐 {timeAgo(item.created_at)} · {item.views || 0} views</div>
          </div>
          <button
            onClick={e => { e.stopPropagation(); whatsAppEnquiry(item, sellerPhone); }}
            style={{ width: 32, height: 32, borderRadius: '50%', background: '#25D366', border: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', flexShrink: 0, boxShadow: '0 3px 10px rgba(37,211,102,0.4)' }}>
            <WaIcon size={14} />
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Main Component ────────────────────────────────────────────────────────────
export default function SellerShop() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { whatsAppEnquiry, formatPrice } = useApp();

  const [seller, setSeller]     = useState(null);
  const [products, setProducts] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [copied, setCopied]     = useState(false);

  useEffect(() => {
    async function fetchShop() {
      setLoading(true);

      const { data: sellerData, error: sellerError } = await supabase
        .from('sellers')
        .select('*')
        .eq('id', id)
        .single();

      if (sellerError || !sellerData) {
        setNotFound(true);
        setLoading(false);
        return;
      }

      setSeller(sellerData);

      const { data: productsData } = await supabase
        .from('products')
        .select('*')
        .eq('seller_id', id)
        .eq('status', 'active')
        .order('created_at', { ascending: false });

      setProducts(productsData || []);
      setLoading(false);
    }

    fetchShop();
  }, [id]);

  const handleWhatsApp = () => {
    const phone = seller.whatsapp || seller.phone;
    const msg = `Hi ${seller.name || seller.shop_name}! I found your shop on FAMUZA Market and I'm interested in your listings.`;
    const clean = String(phone).replace(/\D/g, '');
    const number = clean.startsWith('260') ? clean : `260${clean}`;
    window.open(`https://wa.me/${number}?text=${encodeURIComponent(msg)}`, '_blank');
  };

  const handleShare = () => {
    const url = window.location.href;
    if (navigator.share) {
      navigator.share({ title: `${seller.shop_name || seller.name} on FAMUZA Market`, url });
    } else {
      navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const sellerPhone = seller?.whatsapp || seller?.phone || '';
  const displayName = seller?.shop_name || seller?.business_name || seller?.name || 'FAMUZA Seller';
  const initials = displayName.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2);
  const activeCount = products.length;

  // ── Loading ──
  if (loading) return (
    <div style={{ background: '#0D0D0D', minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ textAlign: 'center', color: 'rgba(245,240,232,0.4)' }}>
        <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>🏪</div>
        <div style={{ fontSize: '0.9rem' }}>Loading shop...</div>
      </div>
    </div>
  );

  // ── Not found ──
  if (notFound) return (
    <div style={{ background: '#0D0D0D', minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '2rem' }}>
      <div style={{ textAlign: 'center', color: 'rgba(245,240,232,0.5)' }}>
        <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>🔍</div>
        <h2 style={{ fontFamily: 'Syne, sans-serif', color: '#F5F0E8', marginBottom: '0.5rem' }}>Shop not found</h2>
        <p style={{ marginBottom: '1.5rem', fontSize: '0.9rem' }}>This seller shop doesn't exist or has been removed.</p>
        <Link to="/listings">
          <button style={{ background: '#E8622A', color: '#fff', border: 'none', borderRadius: 100, padding: '0.75rem 1.5rem', fontWeight: 700, cursor: 'pointer', fontFamily: 'DM Sans, sans-serif' }}>
            Browse all listings
          </button>
        </Link>
      </div>
    </div>
  );

  return (
    <div style={{ background: '#0D0D0D', minHeight: '100vh', color: '#F5F0E8' }}>

      <style>{`
        @keyframes shimmer { 0% { background-position: 200% 0; } 100% { background-position: -200% 0; } }
      `}</style>

      {/* ── Back nav ── */}
      <div style={{ padding: '0.75rem 1rem 0' }}>
        <Link to="/listings" style={{ fontSize: '0.78rem', color: 'rgba(245,240,232,0.4)', textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: '0.3rem' }}>
          ← Browse all listings
        </Link>
      </div>

      {/* ── Seller profile card ── */}
      <div style={{ padding: '1rem' }}>
        <div style={{ background: 'linear-gradient(135deg, #1A1208, #1E1E1E)', border: '0.5px solid rgba(232,98,42,0.2)', borderRadius: 20, padding: '1.5rem', position: 'relative', overflow: 'hidden' }}>

          {/* Background glow */}
          <div style={{ position: 'absolute', top: -40, right: -40, width: 150, height: 150, background: 'radial-gradient(circle, rgba(232,98,42,0.12) 0%, transparent 70%)', pointerEvents: 'none' }} />

          {/* Top row — avatar + info */}
          <div style={{ display: 'flex', gap: '1rem', alignItems: 'flex-start', marginBottom: '1.25rem' }}>
            {/* Avatar */}
            <div style={{ width: 64, height: 64, borderRadius: '50%', background: 'linear-gradient(135deg, #E8622A, #C44A1A)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'Syne, sans-serif', fontWeight: 800, fontSize: '1.4rem', color: '#fff', flexShrink: 0, boxShadow: '0 4px 16px rgba(232,98,42,0.4)' }}>
              {initials}
            </div>

            {/* Name + badges */}
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '0.3rem' }}>
                <h1 style={{ fontFamily: 'Syne, sans-serif', fontSize: '1.2rem', fontWeight: 800, color: '#F5F0E8', margin: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {displayName}
                </h1>
                {seller.verified && (
                  <span style={{ background: 'rgba(37,211,102,0.15)', color: '#25D366', fontSize: '0.62rem', fontWeight: 700, padding: '2px 8px', borderRadius: 100, border: '0.5px solid rgba(37,211,102,0.3)', flexShrink: 0 }}>
                    ✓ Verified
                  </span>
                )}
              </div>

              {seller.name && seller.shop_name && (
                <div style={{ fontSize: '0.78rem', color: 'rgba(245,240,232,0.5)', marginBottom: '0.25rem' }}>{seller.name}</div>
              )}

              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginTop: '0.4rem' }}>
                {seller.location && (
                  <span style={{ fontSize: '0.68rem', color: 'rgba(245,240,232,0.45)', display: 'flex', alignItems: 'center', gap: '0.2rem' }}>
                    📍 {seller.location}
                  </span>
                )}
                <span style={{ fontSize: '0.68rem', color: 'rgba(245,240,232,0.45)' }}>
                  📦 {activeCount} listing{activeCount !== 1 ? 's' : ''}
                </span>
                {seller.tier && (
                  <span style={{ fontSize: '0.62rem', background: 'rgba(196,154,42,0.15)', color: '#C49A2A', padding: '2px 7px', borderRadius: 100, border: '0.5px solid rgba(196,154,42,0.3)', fontWeight: 600 }}>
                    {seller.tier.charAt(0).toUpperCase() + seller.tier.slice(1)} seller
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Action buttons */}
          <div style={{ display: 'flex', gap: '0.75rem' }}>
            <button
              onClick={handleWhatsApp}
              style={{ flex: 1, background: '#25D366', color: '#fff', border: 'none', borderRadius: 100, padding: '0.75rem', fontWeight: 700, fontSize: '0.9rem', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem', fontFamily: 'DM Sans, sans-serif', boxShadow: '0 4px 16px rgba(37,211,102,0.35)' }}>
              <WaIcon size={17} /> Chat on WhatsApp
            </button>
            <button
              onClick={handleShare}
              style={{ background: 'rgba(255,255,255,0.08)', color: copied ? '#25D366' : 'rgba(245,240,232,0.7)', border: '0.5px solid rgba(255,255,255,0.12)', borderRadius: 100, padding: '0.75rem 1rem', fontWeight: 600, fontSize: '0.82rem', cursor: 'pointer', fontFamily: 'DM Sans, sans-serif', whiteSpace: 'nowrap', flexShrink: 0 }}>
              {copied ? '✓ Copied!' : '🔗 Share'}
            </button>
          </div>
        </div>
      </div>

      {/* ── FAMUZA badge ── */}
      <div style={{ padding: '0 1rem 0.75rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', background: '#161616', border: '0.5px solid rgba(255,255,255,0.06)', borderRadius: 10, padding: '0.5rem 0.875rem' }}>
          <span style={{ fontSize: '0.75rem' }}>🛡️</span>
          <span style={{ fontSize: '0.72rem', color: 'rgba(245,240,232,0.5)' }}>
            Listed on <span style={{ color: '#E8622A', fontWeight: 700 }}>FAMUZA Market</span> — Zambia's trusted marketplace
          </span>
        </div>
      </div>

      {/* ── Listings section ── */}
      <div style={{ padding: '0 1rem 3rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem' }}>
          <span style={{ fontFamily: 'Syne, sans-serif', fontSize: '1rem', fontWeight: 800, color: '#F5F0E8' }}>
            🛍️ <span style={{ color: '#E8622A' }}>Listings</span>
            <span style={{ fontSize: '0.75rem', color: 'rgba(245,240,232,0.4)', fontWeight: 400, marginLeft: '0.5rem' }}>({activeCount})</span>
          </span>
        </div>

        {products.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '3rem 1rem', color: 'rgba(245,240,232,0.35)' }}>
            <div style={{ fontSize: '2.5rem', marginBottom: '0.75rem' }}>📦</div>
            <p style={{ fontSize: '0.9rem' }}>No active listings yet.</p>
            <p style={{ fontSize: '0.78rem', marginTop: '0.25rem' }}>Check back soon!</p>
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
            {products.map(item => (
              <ProductCard
                key={item.id}
                item={item}
                navigate={navigate}
                formatPrice={formatPrice}
                whatsAppEnquiry={whatsAppEnquiry}
                sellerPhone={sellerPhone}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
